// PBX migration UI — driven from a LINE, which is what unparked it.
//
// This was parked under ticket #12 with a precise reason: the PBX host belongs to the DID and
// everything else inherits it, so migrating one sip_account on its own produced exactly the
// disagreement the provisioning planner blocks. The note said what it needed — "to be driven from a
// DID rather than a sip_account" (task #230) — and that is how it is now opened: from the line, so
// the number and its extension move together and nothing is left pointing at two hosts.
//
// The saga behind it (cloud/workers/src/lib/migration.js) was rebuilt 2026-09-08 to Kevin's order:
// build the line on the new PBX and CONFIRM it, then move the carrier route, then move the phone in
// GDMS. It reuses the ordinary line provisioner for the build, so there is one implementation of
// "make an extension exist on a host" rather than the two that had silently drifted apart.

import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

// Migrate an extension to a different PBX host. Picks a target PBX, previews
// the plan (preflight blockers + make-before-break steps) via the read-only
// plan endpoint, then executes the saga. See docs/PBX_MIGRATION.md.
export default function MigrateExtensionModal({ account, lineNumber = null, onClose, onDone }) {
  const [target, setTarget] = useState('');
  const [notified, setNotified] = useState(false);
  // An acknowledgement, not an option. The preview is refetched WITH it so the operator sees the
  // plan as it would actually run — including the mailbox step — before committing to anything.
  const [acceptVm, setAcceptVm] = useState(false);
  const hostsQ = useQuery({ queryKey: ['pbx-hosts'], queryFn: () => api.get('/pbx-hosts') });
  const hosts = Array.isArray(hostsQ.data) ? hostsQ.data : (hostsQ.data?.hosts || []);
  const planQ = useQuery({
    queryKey: ['migrate-plan', account.id, target, acceptVm],
    // A GENEROUS TIMEOUT, because this preflight talks to BOTH hosts through their tunnels.
    // Parallelised in lib/migration.js it is a few seconds, but a cold isolate on a slow host can
    // still exceed the default — and the failure mode was the worst kind of misleading: the worker
    // computed a perfectly good plan and the modal said "Request timed out — please try again".
    queryFn: () => api.get(`/sip-accounts/${account.id}/migrate/plan`, {
      to_agent: target, ...(acceptVm ? { accept_voicemail_risk: '1' } : {}),
    }, { timeoutMs: 45_000 }),
    enabled: Boolean(target),
    retry: false,
  });
  const plan = planQ.data;

  // Stage the cutover. 202 → verifying (async); 200 → committed inline (no
  // device); 422/502 reject with the result on err.body (blocked / rolled back).
  // Give it a generous timeout — staging does live GDMS + Commio writes.
  const migrate = useMutation({
    mutationFn: () => api.post(`/sip-accounts/${account.id}/migrate`, { to_agent: target, ...(acceptVm ? { accept_voicemail_risk: true } : {}) }, undefined, { timeoutMs: 60_000 }),
  });
  const staged = migrate.data?.status === 'verifying';

  // Poll the migration status while it's verifying — the cron finalizer flips it
  // to committed/rolled_back once the device (re-)registers or the deadline hits.
  const statusQ = useQuery({
    queryKey: ['migrate-status', account.id],
    queryFn: () => api.get(`/sip-accounts/${account.id}/migrate/status`),
    enabled: staged,
    refetchInterval: (q) => (q?.state?.data?.status === 'verifying' ? 8000 : false),
    refetchOnWindowFocus: false,
  });

  const errBody = migrate.error?.body;
  const liveStatus = statusQ.data?.status || migrate.data?.status || errBody?.status;
  const committed = liveStatus === 'committed';
  const rolledBack = liveStatus === 'rolled_back' || liveStatus === 'failed';
  const verifying = staged && !committed && !rolledBack;
  const newAgentName = plan?.to?.agentName || statusQ.data?.newAgentName;
  const terminal = committed || rolledBack;

  // Refresh the underlying tables once the migration commits.
  useEffect(() => { if (committed && !notified) { setNotified(true); onDone?.(); } }, [committed, notified, onDone]);

  // THE HOST THIS EXTENSION IS ALREADY ON, known before anything is selected.
  //
  // This read `plan?.from?.agentId` alone, and the plan query only runs once a target IS selected
  // — so at the moment the dropdown renders it was always `undefined` and nothing was filtered.
  // The operator was offered the host the line already lives on and got "The extension is already
  // on that PBX host." back for choosing it. Circular: you had to pick a target to find out which
  // one to leave out.
  //
  // The caller knows it, so it is passed in. The plan stays as a fallback for a caller that does
  // not supply it, and confirms it once loaded.
  const currentAgentId = account?.agentId || plan?.from?.agentId;
  const targetHosts = hosts.filter((h) => h.agent_id !== currentAgentId);
  const blockers = plan?.blockers || errBody?.blockers || [];
  // The ones the operator cannot tick past. An acknowledgeable blocker is rendered as the
  // checkbox above; repeating it in "Can't migrate yet" said the same sentence twice.
  const hardBlockers = blockers.filter((b) => !b.acknowledgeable);
  const ackBlocker = blockers.find((b) => b.acknowledgeable === 'accept_voicemail_risk');
  const canMigrate = Boolean(target) && plan?.ok && !migrate.isPending && !staged && !terminal;
  const warnings = statusQ.data?.warnings || migrate.data?.warnings || [];
  // PREFLIGHT warnings are a different thing from run warnings: they say what this migration will
  // do to something the operator did not ask about — a shared DECT base being rebooted, say — and
  // they are only useful BEFORE the button is pressed.
  const planWarnings = plan?.warnings || [];
  const errMsg = statusQ.data?.error || errBody?.error || migrate.error?.message;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">
          Move {lineNumber ? <span className="font-mono">{lineNumber}</span> : `extension ${account.username || `#${account.id}`}`} to another PBX
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          The extension, its phone and any numbers move together. The new host is set up
          first and the old one is only cleared once the phone is up there — if it doesn’t
          come up, the move undoes itself.
        </p>

        {!staged && !terminal && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Target PBX host</label>
            <select
              value={target}
              onChange={(e) => { setTarget(e.target.value); migrate.reset(); }}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select a PBX host…</option>
              {targetHosts.map((h) => (
                <option key={h.agent_id} value={h.agent_id}>{h.name || h.agent_id}</option>
              ))}
            </select>
            {hostsQ.isSuccess && targetHosts.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                There’s no other PBX host to move this extension to.
              </p>
            )}
          </div>
        )}

        {target && !staged && !terminal && planQ.isLoading && <p className="mt-3 text-sm text-gray-400">Checking migration plan…</p>}
        {target && !staged && !terminal && planQ.isError && <p className="mt-3 text-sm text-red-600">Couldn’t load the plan: {planQ.error?.body?.error || planQ.error?.message}</p>}

        {plan && !staged && !terminal && (
          <div className="mt-3 space-y-3">
            {/* ACKNOWLEDGEABLE BLOCKERS. A refusal an operator can read and accept is different from
                one they can only wait out, and the difference belongs on screen: the box appears
                only when the preflight itself says the blocker carries an acknowledgement. */}
            {ackBlocker && (
              <label className="mb-3 flex cursor-pointer items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                <input
                  type="checkbox"
                  checked={acceptVm}
                  onChange={(e) => setAcceptVm(e.target.checked)}
                  className="mt-0.5 cursor-pointer"
                />
                {/* THE SERVER'S WORDS, not a second copy of them. This sentence was hard-coded
                    here, so when the greeting started crossing the tick box went on saying it did
                    not — the blocker, the plan step and the checkbox each describing the same
                    migration differently. The preflight knows what is true for THIS line; it says
                    so in `acknowledgement`. */}
                <span className="text-sm text-amber-900">
                  {ackBlocker?.acknowledgement
                    || 'I understand what does not move across with the mailbox.'}
                </span>
              </label>
            )}
            {planWarnings.length > 0 && (
              <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-sm font-medium text-amber-900">Before you start</p>
                <ul className="mt-1 list-disc pl-5 text-sm text-amber-900">
                  {planWarnings.map((w) => <li key={w.code}>{w.message}</li>)}
                </ul>
              </div>
            )}
            {/* NOT THE ONES THE CHECKBOX ABOVE ALREADY STATES. An acknowledgeable blocker is
                rendered as the tick box — listing it here too printed the same sentence twice,
                one directly above the other, and the second copy read as a refusal the operator
                could do nothing about when the box right above it was the way past. */}
            {hardBlockers.length > 0 ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="text-sm font-semibold text-amber-800">Can’t migrate yet:</div>
                <ul className="mt-1 list-disc pl-5 text-sm text-amber-700">
                  {hardBlockers.map((b) => <li key={b.code}>{b.message}</li>)}
                </ul>
              </div>
            ) : (
              <>
                <ol className="space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                  {plan.steps.map((s) => (
                    <li key={s.id} className="flex gap-2">
                      <span className={`inline-block rounded px-1 text-[10px] font-medium ${
                        s.phase === 'post-commit' ? 'bg-gray-200 text-gray-600' : s.phase === 'commit' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>{s.system}</span>
                      <span>{s.description}</span>
                    </li>
                  ))}
                </ol>
                <p className="text-xs text-gray-500">
                  The phone comes up on the new host over the next 1–3 minutes, and calls may
                  drop briefly. You can close this — it keeps running, and undoes itself if the
                  phone doesn’t come up.
                </p>
              </>
            )}
          </div>
        )}

        {verifying && (
          <div className="mt-3 flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            <span className="mt-0.5 inline-block h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
            <div>
              Cutover staged — waiting for the device to re-register on{' '}
              <span className="font-medium">{newAgentName || 'the new PBX'}</span>. This usually
              takes 1–3 minutes. It’ll commit automatically once the phone is up, or roll back
              if it doesn’t. Safe to close — it keeps running in the background.
            </div>
          </div>
        )}

        {committed && (
          <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            Migrated to <span className="font-medium">{newAgentName}</span> — the device re-registered and the old host was torn down.
            {warnings.length ? ` Note: ${warnings.join(' ')}` : ''}
          </div>
        )}
        {rolledBack && (
          <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {liveStatus === 'failed'
              ? <>Migration couldn’t be finalized and needs manual review. {errMsg}</>
              : <>The device didn’t re-register on the new PBX, so the move was rolled back — the extension is still on its original host.{errMsg ? ` (${errMsg})` : ''}</>}
          </div>
        )}
        {migrate.isError && !terminal && !blockers.length && (
          <p className="mt-2 text-sm text-red-600">{errMsg || 'Migration failed'}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >{terminal || verifying ? 'Close' : 'Cancel'}</button>
          {!staged && !terminal && (
            <button
              type="button"
              disabled={!canMigrate}
              onClick={() => migrate.mutate()}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              {migrate.isPending ? 'Staging…' : 'Migrate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
